import { VoiceTextarea } from '../components/VoiceTextarea';
import { VoiceInput } from '../components/VoiceInput';
// src/modules/CleaningModule.tsx
import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { 
  Home, CheckSquare, Clock, Calendar, ClipboardList, 
  Plus, Trash2, Edit, Save, X, AlertCircle, CheckCircle,
  Bed, Shield, Droplet, FileText, Play, Square
} from 'lucide-react';
import { CleaningChecklist } from './CleaningChecklist';

// ─── INTERFACES ──────────────────────────────────────────────
interface Room {
  id: string;
  name: string;
  floor: 'Ground' | 'First' | 'Second';
}

interface DailyClean {
  id: string;
  roomId: string;
  date: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  staffName: string;
  signedOff: boolean;
  notes: string;
}

interface DeepClean {
  id: string;
  roomId: string;
  date: string;
  nextDue: string;
  staffName: string;
  notes: string;
}

interface MattressAudit {
  id: string;
  roomId: string;
  date: string;
  condition: 'Good' | 'Fair' | 'Poor';
  staffName: string;
  notes: string;
  timeTakenMinutes?: number;
}

interface SideRailAudit {
  id: string;
  roomId: string;
  date: string;
  condition: 'Good' | 'Fair' | 'Poor';
  staffName: string;
  notes: string;
  timeTakenMinutes?: number;
}

const defaultRooms: Room[] = Array.from({ length: 32 }, (_, i) => {
  const num = i + 1;
  const floor = num <= 12 ? 'Ground' : num <= 24 ? 'First' : 'Second';
  return { id: `room-${num}`, name: `Room ${num}`, floor };
});

// ─── COMPONENT ──────────────────────────────────────────────
export function CleaningModule({ user }: { user: User }) {
  const [rooms, setRooms] = useState<Room[]>(defaultRooms);
  const [dailyCleans, setDailyCleans] = useState<DailyClean[]>([]);
  const [deepCleans, setDeepCleans] = useState<DeepClean[]>([]);
  const [mattressAudits, setMattressAudits] = useState<MattressAudit[]>([]);
  const [sideRailAudits, setSideRailAudits] = useState<SideRailAudit[]>([]);
  const [activeTab, setActiveTab] = useState<'daily' | 'deep' | 'mattress' | 'rails' | 'checklist'>('daily');
  const [selectedRoomId, setSelectedRoomId] = useState<string>(rooms[0]?.id || '');
  const [message, setMessage] = useState('');

  // ─── Timer states ──────────────────────────────────────────
  const [mattressTimer, setMattressTimer] = useState<{ [key: string]: { running: boolean; seconds: number; interval: NodeJS.Timeout | null } }>({});
  const [railsTimer, setRailsTimer] = useState<{ [key: string]: { running: boolean; seconds: number; interval: NodeJS.Timeout | null } }>({});

  // ─── Mattress form state ──────────────────────────────────
  const [tempMattress, setTempMattress] = useState({
    roomId: selectedRoomId,
    date: new Date().toISOString().slice(0, 10),
    condition: 'Good' as 'Good' | 'Fair' | 'Poor',
    staff: '',
    notes: '',
  });

  // ─── Rails form state ──────────────────────────────────────
  const [tempRails, setTempRails] = useState({
    roomId: selectedRoomId,
    date: new Date().toISOString().slice(0, 10),
    condition: 'Good' as 'Good' | 'Fair' | 'Poor',
    staff: '',
    notes: '',
  });

  // ─── Timer IDs ─────────────────────────────────────────────
  const mattressTimerId = 'mattress-form';
  const railsTimerId = 'rails-form';

  // ─── Load data ─────────────────────────────────────────────
  useEffect(() => {
    const storedRooms = localStorage.getItem('rmacs_cleaning_rooms');
    if (storedRooms) { try { setRooms(JSON.parse(storedRooms)); } catch {} } else { setRooms(defaultRooms); localStorage.setItem('rmacs_cleaning_rooms', JSON.stringify(defaultRooms)); }

    const storedDaily = localStorage.getItem('rmacs_cleaning_daily');
    if (storedDaily) { try { setDailyCleans(JSON.parse(storedDaily)); } catch { setDailyCleans([]); } } else { setDailyCleans([]); }

    const storedDeep = localStorage.getItem('rmacs_cleaning_deep');
    if (storedDeep) { try { setDeepCleans(JSON.parse(storedDeep)); } catch { setDeepCleans([]); } } else { setDeepCleans([]); }

    const storedMattress = localStorage.getItem('rmacs_cleaning_mattress');
    if (storedMattress) { try { setMattressAudits(JSON.parse(storedMattress)); } catch { setMattressAudits([]); } } else { setMattressAudits([]); }

    const storedRails = localStorage.getItem('rmacs_cleaning_rails');
    if (storedRails) { try { setSideRailAudits(JSON.parse(storedRails)); } catch { setSideRailAudits([]); } } else { setSideRailAudits([]); }
  }, []);

  // ─── Save functions ────────────────────────────────────────
  const saveRooms = (data: Room[]) => { localStorage.setItem('rmacs_cleaning_rooms', JSON.stringify(data)); setRooms(data); };
  const saveDaily = (data: DailyClean[]) => { localStorage.setItem('rmacs_cleaning_daily', JSON.stringify(data)); setDailyCleans(data); };
  const saveDeep = (data: DeepClean[]) => { localStorage.setItem('rmacs_cleaning_deep', JSON.stringify(data)); setDeepCleans(data); };
  const saveMattress = (data: MattressAudit[]) => { localStorage.setItem('rmacs_cleaning_mattress', JSON.stringify(data)); setMattressAudits(data); };
  const saveRails = (data: SideRailAudit[]) => { localStorage.setItem('rmacs_cleaning_rails', JSON.stringify(data)); setSideRailAudits(data); };

  // ─── Add functions ─────────────────────────────────────────
  const addDailyClean = (clean: Omit<DailyClean, 'id'>) => {
    const newClean = { ...clean, id: Date.now().toString() };
    saveDaily([newClean, ...dailyCleans]);
    setMessage('Daily clean logged.');
    setTimeout(() => setMessage(''), 2000);
  };

  const addDeepClean = (deep: Omit<DeepClean, 'id'>) => {
    const newDeep = { ...deep, id: Date.now().toString() };
    saveDeep([newDeep, ...deepCleans]);
    setMessage('Deep clean logged.');
    setTimeout(() => setMessage(''), 2000);
  };

  const addMattressAudit = (audit: Omit<MattressAudit, 'id'>) => {
    const newAudit = { ...audit, id: Date.now().toString() };
    saveMattress([newAudit, ...mattressAudits]);
    setMessage('Mattress audit logged.');
    setTimeout(() => setMessage(''), 2000);
  };

  const addSideRailAudit = (audit: Omit<SideRailAudit, 'id'>) => {
    const newAudit = { ...audit, id: Date.now().toString() };
    saveRails([newAudit, ...sideRailAudits]);
    setMessage('Side rails audit logged.');
    setTimeout(() => setMessage(''), 2000);
  };

  // ─── Timer helpers ────────────────────────────────────────
  const startTimer = (type: 'mattress' | 'rails', id: string) => {
    const setTimer = type === 'mattress' ? setMattressTimer : setRailsTimer;
    const timerState = type === 'mattress' ? mattressTimer : railsTimer;
    if (timerState[id]?.running) return;
    const interval = setInterval(() => {
      setTimer(prev => ({
        ...prev,
        [id]: { ...prev[id], seconds: (prev[id]?.seconds || 0) + 1 }
      }));
    }, 1000);
    setTimer(prev => ({
      ...prev,
      [id]: { running: true, seconds: prev[id]?.seconds || 0, interval }
    }));
  };

  const stopTimer = (type: 'mattress' | 'rails', id: string) => {
    const setTimer = type === 'mattress' ? setMattressTimer : setRailsTimer;
    const timerState = type === 'mattress' ? mattressTimer : railsTimer;
    if (timerState[id]?.interval) {
      clearInterval(timerState[id].interval!);
    }
    setTimer(prev => ({
      ...prev,
      [id]: { ...prev[id], running: false }
    }));
  };

  const formatTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ─── Cleanup timers on unmount ────────────────────────────
  useEffect(() => {
    return () => {
      Object.values(mattressTimer).forEach(t => { if (t.interval) clearInterval(t.interval); });
      Object.values(railsTimer).forEach(t => { if (t.interval) clearInterval(t.interval); });
    };
  }, []);

  // ─── RENDER FUNCTIONS ──────────────────────────────────────

  const renderDailyTab = () => {
    // ... (existing daily tab code – unchanged)
    const today = new Date().toISOString().slice(0,10);
    const todayCleans = dailyCleans.filter(d => d.date === today);
    const cleanedRoomIds = new Set(todayCleans.map(d => d.roomId));
    const pendingRooms = rooms.filter(r => !cleanedRoomIds.has(r.id));

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h3 className="text-lg font-bold text-[#123B36] mb-4">Daily Room Cleaning</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#F7F5EE] rounded-lg p-4 text-center">
              <p className="text-sm text-[#6B7A76]">Total Rooms</p>
              <p className="text-2xl font-bold text-[#123B36]">{rooms.length}</p>
            </div>
            <div className="bg-[#E4F3EC] rounded-lg p-4 text-center">
              <p className="text-sm text-[#6B7A76]">Cleaned Today</p>
              <p className="text-2xl font-bold text-[#1E7F5C]">{todayCleans.length}</p>
            </div>
            <div className="bg-[#FBEFD9] rounded-lg p-4 text-center">
              <p className="text-sm text-[#6B7A76]">Pending</p>
              <p className="text-2xl font-bold text-[#B8720B]">{pendingRooms.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h4 className="font-bold text-[#123B36]">Log Cleaning</h4>
          <form onSubmit={(e) => {
            e.preventDefault();
            const roomId = selectedRoomId;
            const start = (document.getElementById('start') as HTMLInputElement)?.value;
            const end = (document.getElementById('end') as HTMLInputElement)?.value;
            const staff = (document.getElementById('staff') as HTMLInputElement)?.value;
            const notes = (document.getElementById('notes') as HTMLTextAreaElement)?.value;
            if (!roomId || !start || !end || !staff) { setMessage('Fill all required fields.'); return; }
            const startMins = timeToMinutes(start);
            const endMins = timeToMinutes(end);
            const duration = endMins - startMins;
            if (duration <= 0) { setMessage('End time must be after start time.'); return; }
            addDailyClean({
              roomId,
              date: new Date().toISOString().slice(0,10),
              startTime: start,
              endTime: end,
              durationMinutes: duration,
              staffName: staff,
              signedOff: true,
              notes: notes || '',
            });
          }} className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Room</label>
              <select value={selectedRoomId} onChange={(e) => setSelectedRoomId(e.target.value)} className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required>
                {rooms.map(r => <option key={r.id} value={r.id}>{r.name} ({r.floor})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Staff Name</label>
              <VoiceInput id="staff" type="text" placeholder="e.g. Jane" className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Start Time</label>
              <input id="start" type="time" className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#20302C]">End Time</label>
              <input id="end" type="time" className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required />
            </div>
            <div className="col-span-2">
              <VoiceTextarea id="notes" placeholder="Notes (e.g., cleaning tasks, issues)" className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm resize-none" rows={2} />
            </div>
            <div className="col-span-2">
              <button type="submit" className="bg-[#123B36] text-white px-6 py-2 rounded-lg font-semibold">Log Cleaning</button>
            </div>
          </form>
        </div>

        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h4 className="font-bold text-[#123B36]">Today's Cleaning Log</h4>
          {todayCleans.length === 0 ? <p className="text-sm text-[#6B7A76]">No cleaning logged today.</p> : 
            <div className="space-y-2">
              {todayCleans.map(c => {
                const room = rooms.find(r => r.id === c.roomId);
                return (
                  <div key={c.id} className="border-b border-[#EDE8DB] py-2 flex justify-between text-sm">
                    <div>
                      <span className="font-medium">{room?.name || 'Unknown'}</span>
                      <span className="ml-2 text-[#6B7A76]">{c.startTime} – {c.endTime} ({c.durationMinutes} min)</span>
                    </div>
                    <div>
                      <span className="text-green-600">✓ {c.staffName}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          }
        </div>
      </div>
    );
  };

  const renderDeepTab = () => {
    // ... (unchanged)
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h3 className="text-lg font-bold text-[#123B36]">Deep Cleaning Log</h3>
          <p className="text-sm text-[#6B7A76]">Record deep cleans and set next due date.</p>
          <form onSubmit={(e) => {
            e.preventDefault();
            const roomId = selectedRoomId;
            const date = (document.getElementById('deep-date') as HTMLInputElement)?.value;
            const nextDue = (document.getElementById('deep-next') as HTMLInputElement)?.value;
            const staff = (document.getElementById('deep-staff') as HTMLInputElement)?.value;
            const notes = (document.getElementById('deep-notes') as HTMLTextAreaElement)?.value;
            if (!roomId || !date || !nextDue || !staff) { setMessage('Fill all required fields.'); return; }
            addDeepClean({ roomId, date, nextDue, staffName: staff, notes: notes || '' });
          }} className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Room</label>
              <select value={selectedRoomId} onChange={(e) => setSelectedRoomId(e.target.value)} className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required>
                {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Date</label>
              <input id="deep-date" type="date" defaultValue={new Date().toISOString().slice(0,10)} className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Next Due Date</label>
              <input id="deep-next" type="date" className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Staff Name</label>
              <VoiceInput id="deep-staff" type="text" className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required />
            </div>
            <div className="col-span-2">
              <VoiceTextarea id="deep-notes" placeholder="Notes" className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm resize-none" rows={2} />
            </div>
            <div className="col-span-2">
              <button type="submit" className="bg-[#123B36] text-white px-6 py-2 rounded-lg font-semibold">Log Deep Clean</button>
            </div>
          </form>
        </div>
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h4 className="font-bold text-[#123B36]">Deep Clean History</h4>
          {deepCleans.length === 0 ? <p className="text-sm text-[#6B7A76]">No deep cleans recorded.</p> :
            <div className="space-y-2">
              {deepCleans.slice(0, 10).map(d => {
                const room = rooms.find(r => r.id === d.roomId);
                return (
                  <div key={d.id} className="border-b border-[#EDE8DB] py-2 flex justify-between text-sm">
                    <span>{room?.name || 'Unknown'} – {d.date}</span>
                    <span>Next: {d.nextDue}</span>
                    <span className="text-[#6B7A76]">{d.staffName}</span>
                  </div>
                );
              })}
            </div>
          }
        </div>
      </div>
    );
  };

  // ─── Mattress tab (now uses parent state) ─────────────────
  const renderMattressTab = () => {
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      const timerData = mattressTimer[mattressTimerId];
      const minutes = timerData ? Math.ceil(timerData.seconds / 60) : 0;
      addMattressAudit({
        roomId: tempMattress.roomId,
        date: tempMattress.date,
        condition: tempMattress.condition,
        staffName: tempMattress.staff,
        notes: tempMattress.notes,
        timeTakenMinutes: minutes,
      });
      if (timerData?.interval) clearInterval(timerData.interval);
      setMattressTimer(prev => ({ ...prev, [mattressTimerId]: { running: false, seconds: 0, interval: null } }));
      setTempMattress({ ...tempMattress, staff: '', notes: '' });
      setMessage('Mattress audit logged.');
      setTimeout(() => setMessage(''), 2000);
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h3 className="text-lg font-bold text-[#123B36]">Mattress Audits</h3>
          <p className="text-sm text-[#6B7A76]">Record mattress condition checks with time tracking.</p>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Room</label>
              <select value={tempMattress.roomId} onChange={(e) => setTempMattress({ ...tempMattress, roomId: e.target.value })} className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required>
                {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Date</label>
              <input type="date" value={tempMattress.date} onChange={(e) => setTempMattress({ ...tempMattress, date: e.target.value })} className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Condition</label>
              <select value={tempMattress.condition} onChange={(e) => setTempMattress({ ...tempMattress, condition: e.target.value as any })} className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Staff Name</label>
              <VoiceInput value={tempMattress.staff} onChange={(e) => setTempMattress({ ...tempMattress, staff: e.target.value })} className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-[#20302C]">Time Tracking</label>
              <div className="flex items-center gap-3">
                <span className="text-sm font-mono bg-gray-100 px-3 py-1 rounded">
                  ⏱ {mattressTimer[mattressTimerId]?.seconds ? formatTimer(mattressTimer[mattressTimerId].seconds) : '0:00'}
                </span>
                {!mattressTimer[mattressTimerId]?.running ? (
                  <button type="button" onClick={() => startTimer('mattress', mattressTimerId)} className="px-3 py-1 bg-[#123B36] text-white rounded text-sm hover:bg-[#0E2C28]">
                    <Play className="w-3 h-3 inline" /> Start Timer
                  </button>
                ) : (
                  <button type="button" onClick={() => stopTimer('mattress', mattressTimerId)} className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700">
                    <Square className="w-3 h-3 inline" /> Stop Timer
                  </button>
                )}
              </div>
            </div>
            <div className="col-span-2">
              <VoiceTextarea value={tempMattress.notes} onChange={(e) => setTempMattress({ ...tempMattress, notes: e.target.value })} placeholder="Notes" className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm resize-none" rows={2} />
            </div>
            <div className="col-span-2">
              <button type="submit" className="bg-[#123B36] text-white px-6 py-2 rounded-lg font-semibold">Log Mattress Audit</button>
            </div>
          </form>
        </div>
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h4 className="font-bold text-[#123B36]">Mattress Audit History</h4>
          {mattressAudits.length === 0 ? <p className="text-sm text-[#6B7A76]">No audits recorded.</p> :
            <div className="space-y-2">
              {mattressAudits.slice(0, 10).map(a => {
                const room = rooms.find(r => r.id === a.roomId);
                return (
                  <div key={a.id} className="border-b border-[#EDE8DB] py-2 flex justify-between text-sm">
                    <span>{room?.name || 'Unknown'} – {a.date}</span>
                    <span className={`font-semibold ${a.condition === 'Good' ? 'text-green-600' : a.condition === 'Fair' ? 'text-yellow-600' : 'text-red-600'}`}>{a.condition}</span>
                    <span className="text-[#6B7A76]">{a.staffName}</span>
                    {a.timeTakenMinutes && <span className="text-gray-400">⏱ {a.timeTakenMinutes} min</span>}
                  </div>
                );
              })}
            </div>
          }
        </div>
      </div>
    );
  };

  // ─── Rails tab (uses parent state) ──────────────────────
  const renderRailsTab = () => {
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      const timerData = railsTimer[railsTimerId];
      const minutes = timerData ? Math.ceil(timerData.seconds / 60) : 0;
      addSideRailAudit({
        roomId: tempRails.roomId,
        date: tempRails.date,
        condition: tempRails.condition,
        staffName: tempRails.staff,
        notes: tempRails.notes,
        timeTakenMinutes: minutes,
      });
      if (timerData?.interval) clearInterval(timerData.interval);
      setRailsTimer(prev => ({ ...prev, [railsTimerId]: { running: false, seconds: 0, interval: null } }));
      setTempRails({ ...tempRails, staff: '', notes: '' });
      setMessage('Side rails audit logged.');
      setTimeout(() => setMessage(''), 2000);
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h3 className="text-lg font-bold text-[#123B36]">Side Rails Audits</h3>
          <p className="text-sm text-[#6B7A76]">Record side rail condition checks with time tracking.</p>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Room</label>
              <select value={tempRails.roomId} onChange={(e) => setTempRails({ ...tempRails, roomId: e.target.value })} className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required>
                {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Date</label>
              <input type="date" value={tempRails.date} onChange={(e) => setTempRails({ ...tempRails, date: e.target.value })} className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Condition</label>
              <select value={tempRails.condition} onChange={(e) => setTempRails({ ...tempRails, condition: e.target.value as any })} className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Staff Name</label>
              <VoiceInput value={tempRails.staff} onChange={(e) => setTempRails({ ...tempRails, staff: e.target.value })} className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm" required />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-[#20302C]">Time Tracking</label>
              <div className="flex items-center gap-3">
                <span className="text-sm font-mono bg-gray-100 px-3 py-1 rounded">
                  ⏱ {railsTimer[railsTimerId]?.seconds ? formatTimer(railsTimer[railsTimerId].seconds) : '0:00'}
                </span>
                {!railsTimer[railsTimerId]?.running ? (
                  <button type="button" onClick={() => startTimer('rails', railsTimerId)} className="px-3 py-1 bg-[#123B36] text-white rounded text-sm hover:bg-[#0E2C28]">
                    <Play className="w-3 h-3 inline" /> Start Timer
                  </button>
                ) : (
                  <button type="button" onClick={() => stopTimer('rails', railsTimerId)} className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700">
                    <Square className="w-3 h-3 inline" /> Stop Timer
                  </button>
                )}
              </div>
            </div>
            <div className="col-span-2">
              <VoiceTextarea value={tempRails.notes} onChange={(e) => setTempRails({ ...tempRails, notes: e.target.value })} placeholder="Notes" className="w-full rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm resize-none" rows={2} />
            </div>
            <div className="col-span-2">
              <button type="submit" className="bg-[#123B36] text-white px-6 py-2 rounded-lg font-semibold">Log Side Rails Audit</button>
            </div>
          </form>
        </div>
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h4 className="font-bold text-[#123B36]">Side Rails Audit History</h4>
          {sideRailAudits.length === 0 ? <p className="text-sm text-[#6B7A76]">No audits recorded.</p> :
            <div className="space-y-2">
              {sideRailAudits.slice(0, 10).map(a => {
                const room = rooms.find(r => r.id === a.roomId);
                return (
                  <div key={a.id} className="border-b border-[#EDE8DB] py-2 flex justify-between text-sm">
                    <span>{room?.name || 'Unknown'} – {a.date}</span>
                    <span className={`font-semibold ${a.condition === 'Good' ? 'text-green-600' : a.condition === 'Fair' ? 'text-yellow-600' : 'text-red-600'}`}>{a.condition}</span>
                    <span className="text-[#6B7A76]">{a.staffName}</span>
                    {a.timeTakenMinutes && <span className="text-gray-400">⏱ {a.timeTakenMinutes} min</span>}
                  </div>
                );
              })}
            </div>
          }
        </div>
      </div>
    );
  };

  // ─── Helper: time to minutes ──────────────────────────────
  const timeToMinutes = (time: string) => {
    const parts = time.split(':').map(Number);
    return parts[0] * 60 + parts[1];
  };

  // ─── MAIN RENDER ──────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 flex flex-wrap justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#123B36]">Cleaning & Asset Management</h2>
          <p className="text-[#6B7A76]">Daily cleaning, deep cleans, mattress & side rail audits, plus room checklist</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setActiveTab('daily')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'daily' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>Daily</button>
          <button onClick={() => setActiveTab('deep')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'deep' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>Deep Clean</button>
          <button onClick={() => setActiveTab('mattress')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'mattress' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>Mattress</button>
          <button onClick={() => setActiveTab('rails')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'rails' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>Side Rails</button>
          <button onClick={() => setActiveTab('checklist')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'checklist' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>
            <FileText className="w-4 h-4 inline mr-1" /> Checklist
          </button>
        </div>
      </div>

      {message && <div className="bg-[#E4F3EC] text-[#1E7F5C] p-3 rounded-xl text-sm">{message}</div>}

      {activeTab === 'daily' && renderDailyTab()}
      {activeTab === 'deep' && renderDeepTab()}
      {activeTab === 'mattress' && renderMattressTab()}
      {activeTab === 'rails' && renderRailsTab()}
      {activeTab === 'checklist' && <CleaningChecklist user={user} />}
    </div>
  );
}

export default CleaningModule;